import React from 'react'

import {
  usePRFileArtboards,
  useRootNodeAbsoluteExtent,
  PRMarinaFileArtboard,
  PRMarinaLayerType,
} from '@sketch-hq/sketch-web-renderer'

import { ArtboardOverlay } from '../../../../canvas/components/ArtboardOverlay'
import { useInspectorContext } from '../../../FrameView'
import {
  useViewModeState,
  useViewModeOverlayProps,
  useViewModeGlobalEvents,
} from './viewMode.hooks'
import { useInspectModeOverlayProps } from './inspectMode.hooks'
import { Rect } from '../../../../inspector/types'
import { useIsPreviewView } from '../../../views/PreviewView/PreviewContext'
import { useCanvasAnnotations } from '../../../../annotations/hooks'
import { useCanvasInteractions } from '../../../../canvas/components/CanvasInteractions'

type PageCanvasFrameOverlayProps = {
  frame: PRMarinaFileArtboard
  isInspectorActive: boolean
  // View mode props
  framesRefs: React.RefObject<Record<string, HTMLDivElement | null>>
  selectedArtboardUUID: string | null
  setSelectedArtboardUUID: (uuid: string | null) => void
  rootExtent: Rect
  isPreviewView: boolean
  isPlacingDraftAnnotation: boolean
  disableInteractions?: boolean
}

const PageCanvasFrameOverlay = React.memo(function PageCanvasFrameOverlay({
  frame,
  isInspectorActive,
  framesRefs,
  selectedArtboardUUID,
  setSelectedArtboardUUID,
  rootExtent,
  isPreviewView,
  isPlacingDraftAnnotation,
  disableInteractions,
}: PageCanvasFrameOverlayProps) {
  const viewModeProps = useViewModeOverlayProps(
    frame,
    selectedArtboardUUID,
    setSelectedArtboardUUID,
    isPreviewView,
    isPlacingDraftAnnotation
  )
  const inspectModeProps = useInspectModeOverlayProps(frame)

  let overlayProps = viewModeProps
  if (disableInteractions) {
    overlayProps = {
      disableAutoHover: true,
    }
  } else if (isInspectorActive) {
    overlayProps = inspectModeProps
  }

  return (
    <ArtboardOverlay
      ref={(ref: HTMLDivElement | null) => {
        if (framesRefs.current) {
          framesRefs.current[frame.objectId] = ref
        }
      }}
      bounds={frame.bounds}
      artboardName={frame.name}
      artboardUUID={frame.objectId}
      isSymbol={
        frame.getNode()?.getLayer()?.type.value ===
        PRMarinaLayerType.SymbolMaster
      }
      rootExtent={rootExtent}
      {...overlayProps}
    />
  )
})

/**
 * The overlay we show on top ot the page canvas top level frames. The overlay is used to show
 * the artboards' names, cta, outline and listen for interactions on them.
 * Note: we use a unified component for both view modes to use only one React tree and
 * avoid remounting all overlays when switching modes.
 */
export const PageCanvasFramesOverlays = React.memo(
  function PageCanvasFramesOverlays() {
    const { isInspectorActive } = useInspectorContext()
    const topLevelFrames = usePRFileArtboards() ?? []

    const { framesRefs, selectedArtboardUUID, setSelectedArtboardUUID } =
      useViewModeState()

    const rootExtent = useRootNodeAbsoluteExtent()
    const isPreviewView = useIsPreviewView()
    const { isPlacingDraftAnnotation } = useCanvasAnnotations()
    const { isEnabled: isCanvasInteractionsEnabled } = useCanvasInteractions()

    // View mode global events - only active in view mode
    useViewModeGlobalEvents(
      framesRefs,
      selectedArtboardUUID,
      setSelectedArtboardUUID,
      isInspectorActive
    )

    return (
      <>
        {topLevelFrames.map(frame => (
          <PageCanvasFrameOverlay
            key={frame.objectId}
            frame={frame}
            isInspectorActive={isInspectorActive}
            // View mode props
            framesRefs={framesRefs}
            selectedArtboardUUID={selectedArtboardUUID}
            setSelectedArtboardUUID={setSelectedArtboardUUID}
            rootExtent={rootExtent}
            isPreviewView={isPreviewView}
            isPlacingDraftAnnotation={isPlacingDraftAnnotation ?? false}
            disableInteractions={!isCanvasInteractionsEnabled}
          />
        ))}
      </>
    )
  }
)
